'use client';
// Explorer — universal search, blocks, transactions and full details
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Search, Box, Activity, Clock3, Layers, Atom, ShieldCheck, Gem, ArrowUpRight,
} from 'lucide-react';
import {
  fetchJson, usePoll, fmtAmt, fmtInt, fmtHashrate, fmtDuration, timeAgo,
  SectionHeader, TypeBadge, Spinner, Mono, CopyBtn, QuantumBadge, TokenAvatar,
} from './shared';
import type { Net, BlockT, Tx, TokenDetailT } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export function Explorer({ net }: { net: Net }) {
  const { data: blocksData, refresh } = usePoll<{ blocks: BlockT[]; total: number }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=blocks&limit=12`), 3000, [net]);
  const { data: txsData } = usePoll<{ txs: Tx[] }>(
    async () => fetchJson(`/api/explorer?net=${net}&what=txs&limit=14`), 3000, [net]);

  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [openBlock, setOpenBlock] = useState<BlockT | null>(null);
  const [openTx, setOpenTx] = useState<{ tx: Tx; height: number | null; confirmations: number; pending: boolean } | null>(null);
  const [openToken, setOpenToken] = useState<string | null>(null);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await fetchJson<{ type: string; block?: BlockT; tx?: Tx; token?: TokenDetailT; height?: number | null; confirmations?: number; pending?: boolean }>(
        `/api/explorer?net=${net}&what=search&q=${encodeURIComponent(q.trim())}`);
      if (r.type === 'block' && r.block) { setOpenBlock(r.block); }
      else if (r.type === 'tx' && r.tx) { setOpenTx({ tx: r.tx, height: r.height ?? null, confirmations: r.confirmations ?? 0, pending: Boolean(r.pending) }); }
      else if (r.type === 'token' && r.token) { setOpenToken(r.token.symbol); }
      else if (r.type === 'address') { toast.info(`Address ${q.trim().slice(0, 14)}… exists on the network — open its history from wallet details`); }
      else toast.error('No results — search by block height, block hash, transaction id, token symbol, or an AM address');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Search failed');
    } finally { setBusy(false); }
  };

  const loadBlock = async (key: string) => {
    try {
      const r = await fetchJson<{ block: BlockT }>(`/api/explorer?net=${net}&what=block&q=${key}`);
      setOpenBlock(r.block);
    } catch { toast.error('Could not load the block'); }
  };

  const loadTx = async (id: string) => {
    try {
      const r = await fetchJson<{ tx: Tx; height: number | null; confirmations: number; pending: boolean }>(`/api/explorer?net=${net}&what=tx&q=${id}`);
      setOpenTx(r);
    } catch { toast.error('Could not load the transaction'); }
  };

  return (
    <div className="space-y-5">
      <SectionHeader icon={Search} title="AmanChain explorer" desc="Search any block, transaction, token or address — everything is open and transparent" />

      {/* Search */}
      <div className="glass p-4 flex gap-2">
        <Input
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Block height (0, 1, 2…), hash, transaction id, token symbol, or an AM address…"
          className="mono"
        />
        <Button onClick={search} disabled={busy} className="gap-2 font-bold shrink-0">
          <Search className="size-4" /> {busy ? 'Searching…' : 'Search'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Blocks */}
        <div className="glass p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold flex items-center gap-2"><Box className="size-4 text-emerald-400" /> Blocks ({fmtInt(blocksData?.total ?? 0)})</div>
            <Button variant="ghost" size="sm" onClick={refresh} className="text-xs">Refresh</Button>
          </div>
          {!blocksData && <Spinner />}
          <div className="space-y-2 max-h-[30rem] overflow-y-auto pl-1">
            {(blocksData?.blocks ?? []).map(b => (
              <button key={b.hash} onClick={() => setOpenBlock(b)}
                className="w-full text-left rounded-lg border border-border/60 bg-muted/30 p-3.5 hover:border-primary/40 transition">
                <div className="flex items-center justify-between">
                  <span className="font-black text-emerald-400 text-sm" dir="ltr">#{fmtInt(b.height)}</span>
                  <span className="text-[0.65rem] text-muted-foreground flex items-center gap-1"><Clock3 className="size-3" /> {timeAgo(b.timestamp)}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5 gap-2">
                  <Mono>{b.hash.slice(0, 20)}…</Mono>
                  <span className="text-[0.65rem] rounded-full bg-amber-500/10 text-amber-400 px-2 py-0.5" dir="ltr">+{fmtAmt(b.reward)} AMAN</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[0.62rem] text-muted-foreground">
                  <span>{b.transactions.length} txs</span>
                  <span dir="ltr">D{b.difficulty}</span>
                  <span>{fmtHashrate(b.hashRate)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Transactions */}
        <div className="glass p-5">
          <div className="text-sm font-bold mb-3 flex items-center gap-2"><Activity className="size-4 text-sky-400" /> Transactions</div>
          {!txsData && <Spinner />}
          <div className="space-y-2 max-h-[30rem] overflow-y-auto pl-1">
            {(txsData?.txs ?? []).map(t => (
              <button key={t.id + String(t.height)} onClick={() => loadTx(t.id)}
                className="w-full text-left rounded-lg border border-border/60 bg-muted/30 p-3.5 hover:border-primary/40 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <TypeBadge type={t.type} label={t.label} />
                    <QuantumBadge compact={t.type === 'token_transfer'} />
                  </div>
                  <span className={`text-xs font-black ${t.pending ? 'text-sky-400' : ''}`} dir="ltr">
                    {fmtAmt(t.amount)} {t.type.startsWith('token') ? t.data?.symbol : 'AMAN'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <Mono>{t.id.slice(0, 22)}…</Mono>
                  <span className="text-[0.62rem] text-muted-foreground">{t.pending ? 'pending' : `block #${fmtInt(t.height ?? 0)}`}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <BlockDialog block={openBlock} onClose={() => setOpenBlock(null)} onOpenTx={loadTx} />
      <TxDialog data={openTx} onClose={() => setOpenTx(null)} onOpenBlock={loadBlock} />
      <TokenDialog net={net} symbol={openToken} onClose={() => setOpenToken(null)} />
    </div>
  );
}

// ---------- Block details ----------
function BlockDialog({ block, onClose, onOpenTx }: {
  block: BlockT | null; onClose: () => void; onOpenTx: (id: string) => void;
}) {
  return (
    <Dialog open={Boolean(block)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="glass sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Box className="size-5 text-emerald-400" /> Block #{fmtInt(block?.height ?? 0)}</DialogTitle>
          <DialogDescription>Sealed by SHA3-256 proof-of-work</DialogDescription>
        </DialogHeader>
        {block && (
          <div className="space-y-3 text-sm">
            <KV k="Hash" mono v={block.hash} />
            <KV k="Previous hash" mono v={block.prevHash} />
            <KV k="Merkle root" mono v={block.merkleRoot} />
            <KV k="Time" v={new Date(block.timestamp).toLocaleString('en-US', { hour12: false })} />
            <KV k="Nonce" mono v={String(block.nonce)} />
            <KV k="Difficulty" v={String(block.difficulty)} />
            <KV k="Hash rate" v={fmtHashrate(block.hashRate)} />
            <KV k="Mining time" v={fmtDuration(block.miningTime)} />
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/25 p-2.5 text-center">
                <div className="text-sm font-black text-amber-400" dir="ltr">+{fmtAmt(block.reward)}</div><div className="text-[0.58rem] text-muted-foreground">reward</div>
              </div>
              <div className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/25 p-2.5 text-center">
                <div className="text-sm font-black text-rose-400" dir="ltr">{fmtAmt(block.feeBurned)}</div><div className="text-[0.58rem] text-muted-foreground">burned</div>
              </div>
              <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25 p-2.5 text-center">
                <div className="text-sm font-black text-emerald-400" dir="ltr">{block.transactions.length}</div><div className="text-[0.58rem] text-muted-foreground">transactions</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold mb-2 flex items-center gap-1.5"><Layers className="size-3.5" /> Transactions</div>
              <div className="space-y-1.5 max-h-56 overflow-y-auto pl-1">
                {block.transactions.map(t => (
                  <button key={t.id} onClick={() => onOpenTx(t.id)} className="w-full text-left rounded-md border border-border/60 bg-muted/30 p-2.5 hover:border-primary/40 transition">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <TypeBadge type={t.type} label={t.label} />
                        <QuantumBadge compact />
                      </div>
                      <span className="text-xs font-bold text-emerald-400" dir="ltr">{fmtAmt(t.amount)} {t.type.startsWith('token') ? t.data?.symbol : ''}</span>
                    </div>
                    <Mono className="block mt-1">{t.id.slice(0, 26)}…</Mono>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Transaction details ----------
function TxDialog({ data, onClose, onOpenBlock }: {
  data: { tx: Tx; height: number | null; confirmations: number; pending: boolean } | null;
  onClose: () => void; onOpenBlock: (key: string) => void;
}) {
  const t = data?.tx;
  return (
    <Dialog open={Boolean(data)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="glass sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Activity className="size-5 text-sky-400" /> Transaction details</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {data?.pending ? <span className="text-sky-400 font-bold">Pending in the mempool</span> : <span className="text-emerald-400 font-bold">Confirmed — {data?.confirmations} confirmation{data?.confirmations === 1 ? '' : 's'}</span>}
          </DialogDescription>
        </DialogHeader>
        {t && (
          <div className="space-y-3 text-sm">
            <KV k="ID" mono v={t.id} />
            <KV k="Type" v={t.label ?? t.type} />
            {t.from !== 'NETWORK' ? <KV k="From" mono v={t.from} /> : <KV k="From" v="Network (reward)" />}
            <KV k="To" mono v={t.to} />
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25 p-2.5 text-center">
                <div className="text-sm font-black text-emerald-400" dir="ltr">{fmtAmt(t.amount)}</div>
                <div className="text-[0.58rem] text-muted-foreground">amount {t.type.startsWith('token') ? (t.data?.symbol ?? '') : 'AMAN'}</div>
              </div>
              <div className="rounded-lg bg-muted p-2.5 text-center">
                <div className="text-sm font-black" dir="ltr">{fmtAmt(t.fee)}</div><div className="text-[0.58rem] text-muted-foreground">fee</div>
              </div>
              <div className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/25 p-2.5 text-center">
                <div className="text-sm font-black text-rose-400" dir="ltr">{fmtAmt(t.fee * (t.type === 'token_create' ? 1 : 0.5))}</div><div className="text-[0.58rem] text-muted-foreground">of it burned</div>
              </div>
            </div>
            {t.data?.symbol && (
              <div className="rounded-lg bg-violet-500/10 ring-1 ring-violet-500/25 p-3 text-xs">
                <b className="mono text-violet-300" dir="ltr">{t.data.symbol}</b> {t.data.name && `— ${t.data.name}`}
                {t.type === 'token_create' && (
                  <div className="text-muted-foreground mt-1">
                    AMT-20 deployment · max supply {fmtAmt(t.data.maxSupply, 0)} · initial {fmtAmt(t.data.initialSupply, 0)} · {t.data.decimals} decimals
                  </div>
                )}
                {t.tier && <div className="text-muted-foreground mt-1">Stake plan: {t.tier}</div>}
              </div>
            )}
            <KV k="Nonce" mono v={String(t.nonce)} />
            <KV k="Chain-ID" mono v={String(t.chainId)} />
            <KV k="Time" v={new Date(t.timestamp).toLocaleString('en-US', { hour12: false })} />
            <KV k="Public key (Ed25519)" mono v={t.publicKey.slice(0, 40) + (t.publicKey.length > 40 ? '…' : '')} />
            <KV k="Classic signature (64 bytes)" mono v={t.signature.slice(0, 40) + (t.signature.length > 40 ? '…' : '')} />

            {/* Quantum shield */}
            {t.quantumSafe && (
              <div className="rounded-xl bg-cyan-500/5 ring-1 ring-cyan-500/25 p-3.5 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm font-black text-cyan-300">
                    <Atom className="size-4" /> Quantum shield — ML-DSA-87
                  </div>
                  <QuantumBadge />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-muted/40 ring-1 ring-border p-2.5 text-center">
                    <div className="text-xs font-black text-cyan-300" dir="ltr">{fmtInt(t.pqPubBytes ?? 2592)} B</div>
                    <div className="text-[0.6rem] text-muted-foreground">quantum public key</div>
                  </div>
                  <div className="rounded-lg bg-muted/40 ring-1 ring-border p-2.5 text-center">
                    <div className="text-xs font-black text-cyan-300" dir="ltr">{fmtInt(t.pqSigBytes ?? 4627)} B</div>
                    <div className="text-[0.6rem] text-muted-foreground">quantum signature</div>
                  </div>
                </div>
                {t.pqFingerprint && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[0.68rem] text-muted-foreground">Quantum key fingerprint</span>
                    <span className="mono text-[0.68rem] text-cyan-300" dir="ltr">{t.pqFingerprint}<CopyBtn text={t.pqFingerprint} className="inline-flex ml-1" /></span>
                  </div>
                )}
                {t.pqSignature && (
                  <details className="group">
                    <summary className="cursor-pointer text-[0.68rem] font-bold text-cyan-300/80 hover:text-cyan-300 flex items-center gap-1">
                      <ShieldCheck className="size-3" /> Full proof: the quantum signature (hex — {fmtInt(t.pqSignature.length)} chars)
                    </summary>
                    <div className="rounded-md bg-black/40 ring-1 ring-cyan-500/15 p-2.5 mt-1.5">
                      <Mono className="break-all text-[0.58rem] leading-relaxed text-cyan-200/70">{t.pqSignature.slice(0, 500)}{t.pqSignature.length > 500 ? '…' : ''}</Mono>
                    </div>
                  </details>
                )}
                <p className="text-[0.62rem] text-muted-foreground leading-relaxed">
                  Consensus verified the full quantum signature before accepting this transaction — infeasible to forge even with a perfect quantum computer.
                </p>
              </div>
            )}
            {data?.height !== null && data?.height !== undefined && (
              <button onClick={() => onOpenBlock(String(data.height))} className="w-full rounded-lg bg-secondary ring-1 ring-border p-2.5 text-xs font-bold hover:ring-primary/40 transition flex items-center justify-center gap-1.5">
                <Box className="size-3.5" /> View block #{fmtInt(data.height)}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Token quick view (from search) ----------
function TokenDialog({ net, symbol, onClose }: { net: Net; symbol: string | null; onClose: () => void }) {
  const { data } = usePoll<{ token: TokenDetailT } | null>(
    async () => (symbol ? fetchJson(`/api/explorer?net=${net}&what=token&q=${symbol}`) : Promise.resolve(null)),
    4000, [net, symbol],
  );
  const t = data?.token;
  return (
    <Dialog open={Boolean(symbol)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="glass sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t ? <TokenAvatar symbol={t.symbol} logo={t.logo} size={34} /> : <Gem className="size-5 text-violet-400" />}
            {t?.name ?? 'Token'}
            {t?.stable && <span className="rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-300 px-2 py-0.5 text-[0.6rem] font-black">STABLE · peg {fmtAmt(t.pegAman ?? 1, 4)} AMAN</span>}
          </DialogTitle>
          <DialogDescription className="mono">{symbol}</DialogDescription>
        </DialogHeader>
        {t && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <KV k="Total supply" v={fmtAmt(t.totalSupply, 0)} />
              <KV k="Max supply" v={fmtAmt(t.maxSupply, 0)} />
              <KV k="Burned" v={fmtAmt(t.burned, 0)} />
              <KV k="Holders" v={fmtInt(t.holders)} />
            </div>
            <div>
              <div className="text-xs font-bold mb-2 flex items-center gap-1.5"><ArrowUpRight className="size-3.5" /> Top holders</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {t.topHolders.map((h, i) => (
                  <div key={h.address} className="flex items-center justify-between rounded-lg bg-muted/30 ring-1 ring-border/60 px-3 py-2 text-xs">
                    <span>#{i + 1} <Mono>{h.address.slice(0, 14)}…</Mono></span>
                    <span className="font-black" dir="ltr">{fmtAmt(h.balance, 2)}</span>
                  </div>
                ))}
              </div>
            </div>
            {t.description && <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/50 pb-2">
      <span className="text-xs text-muted-foreground shrink-0">{k}</span>
      <span className={`text-xs text-right break-all ${mono ? 'mono' : 'font-medium'}`} dir="ltr">
        {v}
        {mono && <CopyBtn text={v} className="inline-flex ml-1 align-middle" />}
      </span>
    </div>
  );
}
